import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { FilterMatchMode } from "primereact/api";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useDispatch, useSelector } from "react-redux";
import { InputText } from "primereact/inputtext";
import { getPaymentRequestList } from "../../../../../redux/slices/payment_request_slice";
import * as XLSX from "xlsx";
import { FileUpload } from "primereact/fileupload";
import axios from "axios";
import appUrl from "../../../constants/appUrl";
import { toast } from "react-toastify";
import decryptData from "../../../services/decryptData";
import { Dropdown } from "primereact/dropdown";
import CustomPageHeader from "../../../components/custom_page_header";
import { Paginator } from "primereact/paginator";
import { Dialog } from "primereact/dialog";

const statusList = [
    { status: "Pending", value: "pending" },
    { status: "Paid", value: "paid" },
];

const mobileBodyTemplate = (rowData) => {
    return `+${rowData.userId.mobile}`;
};

export default function PaymentRequest({ permissions }) {
    const { data: list, count } = useSelector((state) => state.paymentRequest);
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [selectedPaymentsRows, setSelectedPaymentsRows] = useState(null);
    const [rows, setRows] = useState(50);
    const [first, setFirst] = useState(1);
    const [pageNumber, setPageNumber] = useState(1);
    const [status, setStatus] = useState("pending");
    const dispatch = useDispatch();
    const fileUploadRef = useRef(null);
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        dispatch(getPaymentRequestList({ page: 1, status, limit: rows, searchText: globalFilterValue }));
    }, [status]);

    useEffect(() => {
        dispatch(getPaymentRequestList({ page: pageNumber, status, limit: rows, searchText: globalFilterValue }));
    }, [globalFilterValue]);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        setGlobalFilterValue(value);
        dispatch(getPaymentRequestList({ page: pageNumber, status, limit: rows, searchText: value }));
    };

    const onStatusChange = (e) => {
        setStatus(e.value);
        setSelectedPaymentsRows(null); // Reset selected rows
        dispatch(getPaymentRequestList({ page: 1, status: e.value, limit: rows, searchText: globalFilterValue }));
    };

    const exportSelectedRowsToExcel = () => {
        if (!selectedPaymentsRows || selectedPaymentsRows.length === 0) {
            toast.info("Please select rows to export.");
            return;
        }

        const excelData = selectedPaymentsRows.map((row) => ({
            "First Name": row?.userId?.firstName || "",
            "Last Name": row?.userId?.lastName || "",
            Mobile: row?.userId?.mobile || "",
            Email: row?.userId?.email || "",
            Amount: row?.amount || "",
            "Transaction Id": row?.transactionId || "",
            UserId: row?.userId?._id || "",
            Status: row?.status || "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Payment Requests");
        XLSX.writeFile(workbook, "selected_payment_requests.xlsx");
    };

    const onUpload = async () => {
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append("file", selectedFile);
        try {
            const response = await axios.post(appUrl.baseUrl + appUrl.uploadPaymentRequest, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            let decryptedData = decryptData(response?.data?.cipher);
            if (response.status === 200) {
                toast.success(decryptedData?.msg);
                fileUploadRef.current.clear(); // Reset the FileUpload component
                dispatch(getPaymentRequestList({ page: pageNumber, status, limit: rows, searchText: globalFilterValue })); // Refresh the list
            }
        } catch (error) {
            let decryptedData = decryptData(error?.response?.data?.cipher);
            toast.error(decryptedData.msg);
            fileUploadRef.current.clear(); // Reset the FileUpload component
            setSelectedFile(null); // Reset the selected file state
        }
        setShowUploadDialog(false);
        setSelectedFile(null);
    };

    const onFileSelect = (event) => {
        const uploadedFile = event.files[0];
        setSelectedFile(uploadedFile);
        setShowUploadDialog(true);
    };

    const hideDialog = () => {
        setShowUploadDialog(false);
        setSelectedFile(null); // Reset the selected file state
        fileUploadRef.current.clear(); // Clear the FileUpload component
    };

    const onPageChange = (event) => {
        setFirst(event.first);
        setPageNumber(event.first / event.rows);
        setRows(event.rows);
        dispatch(getPaymentRequestList({ page: event.first / event.rows, status, limit: event.rows, searchText: globalFilterValue }));
    };

    return (
        <div className="card">
            <div className="col-12 flex gap-5 justify-content-between flex-wrap mb-3">
                <div className="col-3  mb-3">
                    <CustomPageHeader title={"Payment Request"} />
                </div>
                <div className="mr-4 col-8 text-right flex flex-wrap  mb-3">
                    <div className="p-grid  mb-3">
                        <div className="p-col">
                            <Dropdown className="ml-3" value={status} options={statusList} optionLabel="status" optionValue="value" onChange={onStatusChange} placeholder="Select a Status" />
                        </div>
                    </div>
                    <span className="p-input-icon-right mx-3  mb-3">
                        <InputText className="mbl_view" value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Search" />
                    </span>
                    <Button style={{ fontSize: "14px" }} className="p-button-success ml-2  mb-3 p-button-rounded p-button-sm" label="Export to Excel" iconPos="left" icon="pi pi-download" onClick={exportSelectedRowsToExcel} />
                    <FileUpload
                        ref={fileUploadRef}
                        className="p-button-success ml-2  mb-3 p-button-rounded p-button-sm file-upload-custom"
                        name="file"
                        customUpload
                        uploadHandler={onFileSelect}
                        accept=".xlsx,.xls"
                        maxFileSize={1000000}
                        chooseLabel="Upload Excel File"
                        mode="basic"
                        icon="pi pi-upload"
                        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    />
                </div>
            </div>
            <DataTable filter rows={50} value={list} responsiveLayout="scroll" emptyMessage="No record found." selectionMode="checkbox" selection={selectedPaymentsRows} onSelectionChange={(e) => setSelectedPaymentsRows(e.value)} dataKey="_id" stripedRows>
                <Column selectionMode="multiple" style={{ width: "3em", textAlign: "center", verticalAlign: "middle" }} />
                <Column field="transactionId" header="Transaction Id" headerStyle={{ textAlign: "center" }} bodyStyle={{ textAlign: "start", paddingLeft: "14px" }} />
                <Column field="userId.firstName" header="First Name" headerStyle={{ textAlign: "center" }} bodyStyle={{ textAlign: "start", paddingLeft: "14px" }} />
                <Column field="userId.lastName" header="Last Name" headerStyle={{ textAlign: "center" }} bodyStyle={{ textAlign: "start", paddingLeft: "14px" }} />
                <Column field="userId.mobile" header="Mobile" headerStyle={{ textAlign: "center" }} bodyStyle={{ textAlign: "start", paddingLeft: "14px" }} body={mobileBodyTemplate} />
                <Column field="userId.email" header="Email" headerStyle={{ textAlign: "center" }} bodyStyle={{ textAlign: "start", paddingLeft: "14px" }} />
                <Column field="paymentGatewayId.name" header="Name" headerStyle={{ textAlign: "center" }} bodyStyle={{ textAlign: "start", paddingLeft: "14px" }} />
                <Column field="amount" header="Total Amount(PKR)" headerStyle={{ textAlign: "center" }} bodyStyle={{ textAlign: "start", paddingLeft: "14px" }} />
            </DataTable>

            <Paginator first={first} rows={rows} totalRecords={count} onPageChange={onPageChange} rowsPerPageOptions={[10, 20, 30, 50]} />

            <Dialog
                header="Confirm Upload"
                visible={showUploadDialog}
                style={{ width: "350px" }}
                footer={
                    <div>
                        <Button label="No" icon="pi pi-times" onClick={hideDialog} className="p-button-text" />
                        <Button label="Yes" icon="pi pi-check" onClick={onUpload} autoFocus />
                    </div>
                }
                onHide={hideDialog}
            >
                <p>Are you sure you want to upload this file?</p>
            </Dialog>
        </div>
    );
}
